/**
 * Delphi's FormatFloat and FormatDateTime, for the formats FrameTracker prints.
 *
 * The report registry copies each printed field's DisplayFormat verbatim from the
 * .dfm ('$#,0.00;-$#,0.00', '###,###', '0 %', 'mm/dd/yy'), so the screen has to
 * read those strings the way the Delphi runtime did rather than approximate them
 * with toLocaleString. Pure functions, no Vue: checked by
 * scripts/report-checks.mjs (node scripts/report-checks.mjs).
 *
 * FormatFloat, as implemented here:
 *   - Up to three sections split on ';' (outside quotes): positive; negative; zero.
 *     An empty or missing negative section uses the positive one with a leading
 *     '-'; an empty or missing zero section uses the positive one. A value that
 *     rounds to zero at the section's decimals prints with the zero section.
 *   - '0' prints a digit or a 0; '#' prints a digit or nothing. With no '0' before
 *     the decimal point a zero integer part prints nothing ('#.00' gives '.50',
 *     '#,###' gives '' for 0).
 *   - '.' is the decimal point, printed only when a decimal digit follows it.
 *   - ',' anywhere in the section turns on thousands grouping.
 *   - "xx" or 'xx' is literal text; every other character is literal too,
 *     including '$', '%', spaces and parentheses. '%' does NOT multiply.
 *   - Rounding is half away from zero on the 15-significant-digit decimal value,
 *     as FloatToDecimal does, so 1.005 at two places is 1.01.
 *   Not implemented (no FrameTracker report uses them): scientific E+/E- formats.
 *
 * FormatDateTime, as implemented here (case-insensitive like Delphi):
 *   d dd ddd dddd ddddd(m/d/yyyy), m mm mmm mmmm, yy yyyy, h hh, n nn, s ss,
 *   z zzz, am/pm a/p ampm, '/' and ':' separators, quoted literals. 'm' right
 *   after an hour token means minutes, as in Delphi.
 */

const SHORT_DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
const LONG_DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']
const SHORT_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
const LONG_MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August',
    'September', 'October', 'November', 'December']

// Parsed number formats by format string; a page of 1,000 lines formats the
// same few strings tens of thousands of times.
const floatCache = new Map()

/** Splits a format on ';' outside quotes. */
function splitSections(format) {
    const sections = []
    let current = ''
    let quote = null
    for (const ch of format) {
        if (quote) {
            if (ch === quote) quote = null
            current += ch
        } else if (ch === '"' || ch === "'") {
            quote = ch
            current += ch
        } else if (ch === ';') {
            sections.push(current)
            current = ''
        } else {
            current += ch
        }
    }
    sections.push(current)
    return sections
}

/**
 * One section as tokens: {t: 'lit', v}, {t: '0'}, {t: '#'}, {t: '.'}, plus what
 * the digits need to know: grouping, placeholders either side of the point and
 * the minimum digits each side.
 */
function parseSection(section) {
    const tokens = []
    let grouping = false
    let pointSeen = false
    let intCount = 0
    let fracCount = 0
    let minInt = 0
    let firstIntZero = -1
    let lastFracZero = 0

    for (let i = 0; i < section.length; i++) {
        const ch = section[i]
        if (ch === '"' || ch === "'") {
            const end = section.indexOf(ch, i + 1)
            const text = end === -1 ? section.slice(i + 1) : section.slice(i + 1, end)
            tokens.push({t: 'lit', v: text})
            i = end === -1 ? section.length : end
        } else if (ch === '0' || ch === '#') {
            tokens.push({t: ch})
            if (pointSeen) {
                fracCount++
                if (ch === '0') lastFracZero = fracCount
            } else {
                if (ch === '0' && firstIntZero === -1) firstIntZero = intCount
                intCount++
            }
        } else if (ch === '.' && !pointSeen) {
            pointSeen = true
            tokens.push({t: '.'})
        } else if (ch === ',') {
            grouping = true
        } else {
            tokens.push({t: 'lit', v: ch})
        }
    }

    if (firstIntZero !== -1) minInt = intCount - firstIntZero

    return {tokens, grouping, intCount, fracCount, minInt, minFrac: lastFracZero,
        hasDigits: intCount + fracCount > 0}
}

function parsedFloat(format) {
    let parsed = floatCache.get(format)
    if (!parsed) {
        const raw = splitSections(format)
        parsed = raw.map(s => ({raw: s, ...parseSection(s)}))
        floatCache.set(format, parsed)
    }
    return parsed
}

/**
 * |value| rounded half away from zero to `places` decimals, as digit strings,
 * working on the 15-significant-digit decimal form so binary noise (1.005 stored
 * as 1.00499...) rounds the way Delphi's FloatToDecimal does.
 */
function roundDecimal(value, places) {
    const abs = Math.abs(value)
    if (abs === 0) return {int: '', frac: '0'.repeat(places), zero: true}

    // toPrecision(15) is either fixed or exponential notation; normalize both to
    // a digit string and the position of the decimal point within it.
    const p = abs.toPrecision(15)
    let digits
    let point
    const e = p.indexOf('e')
    if (e === -1) {
        const dot = p.indexOf('.')
        digits = p.replace('.', '')
        point = dot === -1 ? p.length : dot
    } else {
        const mantissa = p.slice(0, e)
        const exp = Number(p.slice(e + 1))
        digits = mantissa.replace('.', '')
        point = 1 + exp
    }

    // Pad so the point sits inside the string with at least `places` decimals.
    if (point <= 0) {
        digits = '0'.repeat(1 - point) + digits
        point = 1
    }
    if (digits.length < point + places + 1) digits += '0'.repeat(point + places + 1 - digits.length)

    const keep = point + places
    let kept = digits.slice(0, keep).split('').map(Number)
    if (Number(digits[keep]) >= 5) {
        let i = kept.length - 1
        while (i >= 0) {
            if (kept[i] === 9) {
                kept[i] = 0
                i--
            } else {
                kept[i]++
                break
            }
        }
        if (i < 0) {
            kept.unshift(1)
            point++
        }
    }

    const all = kept.join('')
    const int = all.slice(0, point).replace(/^0+/, '')
    const frac = all.slice(point)
    return {int, frac, zero: /^0*$/.test(all)}
}

function applySection(sec, rounded) {
    if (!sec.hasDigits) {
        return sec.tokens.map(tok => (tok.t === 'lit' ? tok.v : '')).join('')
    }

    let intDigits = rounded.int
    if (intDigits.length < sec.minInt) intDigits = '0'.repeat(sec.minInt - intDigits.length) + intDigits

    let frac = rounded.frac.slice(0, sec.fracCount)
    while (frac.length > sec.minFrac && frac.endsWith('0')) frac = frac.slice(0, -1)

    const n = intDigits.length
    const offset = sec.intCount - n
    let out = ''
    let intSeen = 0
    let fracSeen = 0
    let afterPoint = false

    const emitDigit = idx => {
        out += intDigits[idx]
        const right = n - idx - 1
        if (sec.grouping && right > 0 && right % 3 === 0) out += ','
    }

    sec.tokens.forEach(tok => {
        if (tok.t === 'lit') {
            out += tok.v
        } else if (tok.t === '.') {
            // A format with no integer placeholders ('.00') still prints the
            // integer digits, ahead of the point.
            if (sec.intCount === 0) for (let k = 0; k < n; k++) emitDigit(k)
            afterPoint = true
            if (frac.length > 0) out += '.'
        } else if (!afterPoint) {
            // Integer placeholder: the first one also carries any digits the
            // format has no room for.
            if (intSeen === 0 && offset < 0) {
                for (let k = 0; k <= -offset; k++) emitDigit(k)
            } else if (intSeen - offset >= 0) {
                emitDigit(intSeen - offset)
            }
            intSeen++
        } else {
            if (fracSeen < frac.length) out += frac[fracSeen]
            fracSeen++
        }
    })

    return out
}

/** Whether a format is 'General': no DisplayFormat in FrameTracker. */
export function isGeneral(format) {
    return String(format).toLowerCase() === 'general'
}

/**
 * Delphi FloatToStr: up to 15 significant digits, no thousands separator, no
 * trailing zeros ('9.0000' prints 9). What a field with no DisplayFormat prints.
 */
export function floatToStr(value) {
    if (value === null || value === undefined || value === '') return ''
    const num = typeof value === 'number' ? value : Number(String(value).trim())
    if (!Number.isFinite(num)) return String(value)
    return String(Number(num.toPrecision(15)))
}

/**
 * Delphi FormatFloat. A value that is not a number is returned unchanged as a
 * string; null and undefined give ''. The format 'General' is FloatToStr.
 */
export function formatFloat(format, value) {
    if (value === null || value === undefined || value === '') return ''
    if (isGeneral(format)) return floatToStr(value)
    const num = typeof value === 'number' ? value : Number(String(value).trim())
    if (!Number.isFinite(num)) return String(value)

    const sections = parsedFloat(format)
    const sectionFor = index => {
        const sec = sections[index]
        return sec && sec.raw !== '' ? sec : null
    }

    let index = num === 0 ? 2 : (num < 0 ? 1 : 0)
    let sec = sectionFor(index) || sections[0]
    let rounded = roundDecimal(num, sec.fracCount)

    if (rounded.zero && index !== 2) {
        index = 2
        sec = sectionFor(2) || sections[0]
        rounded = roundDecimal(num, sec.fracCount)
    }

    const sign = index === 1 && !sectionFor(1) ? '-' : ''
    return sign + applySection(sec, rounded)
}

/**
 * A date or datetime value as wall-clock parts. The API returns MySQL strings
 * ('2026-06-01', '2026-06-01 13:45:00') or ISO; they are read as written, never
 * through Date(), which shifts a bare ISO date into UTC and shows the previous
 * day west of Greenwich. A Date object is taken in local time.
 */
export function dateParts(value) {
    if (value instanceof Date) {
        if (isNaN(value.getTime())) return null
        return {
            y: value.getFullYear(), m: value.getMonth() + 1, d: value.getDate(),
            h: value.getHours(), n: value.getMinutes(), s: value.getSeconds(), z: value.getMilliseconds(),
        }
    }
    const match = /^(\d{4})-(\d{1,2})-(\d{1,2})(?:[T ](\d{1,2}):(\d{2})(?::(\d{2})(?:\.(\d{1,6}))?)?)?/.exec(String(value).trim())
    if (!match) return null
    return {
        y: Number(match[1]), m: Number(match[2]), d: Number(match[3]),
        h: Number(match[4] || 0), n: Number(match[5] || 0), s: Number(match[6] || 0),
        z: match[7] ? Number(match[7].slice(0, 3).padEnd(3, '0')) : 0,
    }
}

function weekday(parts) {
    // Zeller-free: the UTC constructor only does calendar arithmetic here.
    return new Date(Date.UTC(parts.y, parts.m - 1, parts.d)).getUTCDay()
}

const pad = (v, len) => String(v).padStart(len, '0')

/**
 * Delphi FormatDateTime. A value that cannot be read as a date is returned
 * unchanged as a string; null, undefined and '' give ''.
 */
export function formatDateTime(format, value) {
    if (value === null || value === undefined || value === '') return ''
    const parts = dateParts(value)
    if (!parts) return String(value)

    // am/pm anywhere switches the hours to 12-hour time, as in Delphi.
    const twelve = /am\/pm|a\/p|ampm/i.test(format.replace(/"[^"]*"|'[^']*'/g, ''))
    let out = ''
    let lastWasHour = false
    let i = 0

    const run = ch => {
        let len = 0
        while (format[i + len] && format[i + len].toLowerCase() === ch) len++
        return len
    }

    while (i < format.length) {
        const ch = format[i]
        const lower = ch.toLowerCase()

        if (ch === '"' || ch === "'") {
            const end = format.indexOf(ch, i + 1)
            out += end === -1 ? format.slice(i + 1) : format.slice(i + 1, end)
            i = end === -1 ? format.length : end + 1
            continue
        }

        if (format.slice(i, i + 5).toLowerCase() === 'am/pm') {
            const text = parts.h < 12 ? 'am' : 'pm'
            out += format.slice(i, i + 5) === format.slice(i, i + 5).toUpperCase() ? text.toUpperCase() : text
            i += 5
            continue
        }
        if (format.slice(i, i + 3).toLowerCase() === 'a/p') {
            const text = parts.h < 12 ? 'a' : 'p'
            out += ch === 'A' ? text.toUpperCase() : text
            i += 3
            continue
        }
        if (format.slice(i, i + 4).toLowerCase() === 'ampm') {
            out += parts.h < 12 ? 'AM' : 'PM'
            i += 4
            continue
        }

        if (lower === 'd') {
            const len = run('d')
            if (len === 1) out += parts.d
            else if (len === 2) out += pad(parts.d, 2)
            else if (len === 3) out += SHORT_DAYS[weekday(parts)]
            else if (len === 4) out += LONG_DAYS[weekday(parts)]
            else out += parts.m + '/' + parts.d + '/' + parts.y
            i += len
            lastWasHour = false
            continue
        }
        if (lower === 'm') {
            const len = run('m')
            if (lastWasHour && len <= 2) {
                out += len === 1 ? parts.n : pad(parts.n, 2)
            } else if (len === 1) out += parts.m
            else if (len === 2) out += pad(parts.m, 2)
            else if (len === 3) out += SHORT_MONTHS[parts.m - 1]
            else out += LONG_MONTHS[parts.m - 1]
            i += len
            lastWasHour = false
            continue
        }
        if (lower === 'y') {
            const len = run('y')
            out += len <= 2 ? pad(parts.y % 100, 2) : pad(parts.y, 4)
            i += len
            lastWasHour = false
            continue
        }
        if (lower === 'h') {
            const len = run('h')
            let hour = parts.h
            if (twelve) hour = hour % 12 === 0 ? 12 : hour % 12
            out += len === 1 ? hour : pad(hour, 2)
            i += len
            lastWasHour = true
            continue
        }
        if (lower === 'n') {
            const len = run('n')
            out += len === 1 ? parts.n : pad(parts.n, 2)
            i += len
            lastWasHour = false
            continue
        }
        if (lower === 's') {
            const len = run('s')
            out += len === 1 ? parts.s : pad(parts.s, 2)
            i += len
            lastWasHour = false
            continue
        }
        if (lower === 'z') {
            const len = run('z')
            out += len >= 3 ? pad(parts.z, 3) : parts.z
            i += len
            lastWasHour = false
            continue
        }

        // '/' and ':' are the US separators; spaces keep an hour token's claim
        // on a following 'm' (Delphi's 'hh mm' is hours and minutes too).
        if (ch !== ' ' && ch !== ':') lastWasHour = false
        out += ch
        i++
    }

    return out
}

/** Delphi DateToStr with US settings: m/d/yyyy. */
export function dateToStr(value) {
    return formatDateTime('m/d/yyyy', value)
}

/**
 * Whether a format string is a date format: no digit placeholders, and at least
 * one date or time letter outside quotes.
 */
export function isDateFormat(format) {
    const bare = String(format).replace(/"[^"]*"|'[^']*'/g, '')
    return !/[0#]/.test(bare) && /[dmyhn]/i.test(bare)
}

/**
 * A value printed with a FrameTracker DisplayFormat: dates through
 * FormatDateTime, everything else through FormatFloat. `isDate` forces the date
 * path for a date column.
 */
export function formatWith(format, value, isDate = false) {
    if (isGeneral(format)) return floatToStr(value)
    if (isDate || isDateFormat(format)) return formatDateTime(format, value)
    return formatFloat(format, value)
}
